import { useTestStore } from '../store/useTestStore';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { QuestionDataLocalStorage } from '../lib/localStorage/questionDataLocalStorage';

/**
 * Cleans up all test-related data from stores and localStorage
 * Should be called after test data has been sent/saved
 */
export const cleanupTestData = async (testId?: string | null) => {
  console.log('🧹 Cleaning up test data for testId:', testId);
  
  // Clear test store data
  const testStore = useTestStore.getState();
  testStore.setCurrentTestId(null);
  testStore.setCurrentQuestionId(null);
  testStore.setQuestions([]);
  testStore.setGroups([]);
  testStore.setTestTitle('');
  testStore.setTotalTime(undefined);
  testStore.clearAnswers();
  testStore.setTestCompleted(false);
  testStore.setCurrentQuestionIndex(0);
  testStore.setSelectedGroupIndex(null);
  
  // Clear whiteboard store data
  const whiteboardStore = useWhiteboardStore.getState();
  whiteboardStore.setQuestionDataCache(new Map());
  whiteboardStore.setCurrentQuestionId(null);
  whiteboardStore.clearAllDrawingHistory();
  whiteboardStore.setOpticalAnswers({});
  whiteboardStore.setEliminatedChoices({});
  whiteboardStore.clearAllCanvasContent();
  
  // Clear localStorage test data
  if (testId) {
    // Remove test resume data
    localStorage.removeItem(`test_resume_${testId}`);
    
    // Clear question data from localStorage
    try {
      // Clear all localStorage items that match question data pattern
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes(testId.toString())) {
          // Check if this is a question data key
          if (key.includes('_') && !key.startsWith('test_')) {
            keysToRemove.push(key);
          }
        }
      }
      
      // Remove all matching keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing question data from IndexedDB:', error);
    }
  }
  
  // Clear any optical answers stored separately (in some test pages)
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('optical_') || key === 'all_answers' || key === 'test_completion' || key === 'unified_test_completion')) {
      keysToRemove.push(key);
    }
  }
  
  // Remove all identified keys
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  console.log('✅ Test data cleanup completed');
};

/**
 * Force clear ALL canvas content including question backgrounds
 * Used specifically for WebView test transitions
 */
const forceCanvasClear = () => {
  const whiteboardStore = useWhiteboardStore.getState();
  const canvas = whiteboardStore.canvasRef;
  
  if (canvas) {
    console.log('🧹 Force clearing ALL canvas content');
    
    // Get all objects before clearing
    const allObjects = [...canvas.getObjects()];
    console.log('📊 Found objects to clear:', allObjects.map(obj => ({
      type: obj.type,
      name: (obj as any).name,
      questionId: (obj as any).questionId
    })));
    
    // Method 1: Try to remove all objects individually first
    allObjects.forEach(obj => {
      try {
        canvas.remove(obj);
      } catch (error) {
        console.warn('Error removing object:', error);
      }
    });
    
    // Method 2: Nuclear option - clear everything
    canvas.clear();
    
    // Method 3: Reset all canvas state
    canvas.backgroundColor = 'white';
    canvas.selection = false;
    canvas.hoverCursor = 'default';
    canvas.defaultCursor = 'default';
    
    // Force re-render
    canvas.requestRenderAll();
    
    // Verify clearing worked
    const remainingObjects = canvas.getObjects();
    if (remainingObjects.length > 0) {
      console.warn('⚠️ Some objects still remain after clearing:', remainingObjects.length);
    } else {
      console.log('✅ Canvas completely cleared');
    }
  }
};

/**
 * Aggressive localStorage cleanup for WebView test transitions
 */
const forceLocalStorageClear = () => {
  console.log('🧹 Force clearing ALL question-related localStorage');
  
  const keysToRemove: string[] = [];
  const patterns = [
    /^\d+_\d+_\w+-\w+-\w+-\w+-\w+_\w+-\w+-\w+-\w+-\w+$/, // Question data pattern
    /^questionData_/, // Old format question data
    /^optical_/, // Optical answers
    /^test_/, // Test related data including test_resume_
    /^all_answers$/, // Answer cache
    /^test_completion$/, // Test completion
    /^unified_test_completion$/, // Unified test completion
    /^questionData_syncStatus$/, // Sync status
    /^questionData_migrationComplete/, // Migration flags
    /^tahta_webview_props$/, // WebView props
    /^tahta_webview_recovery$/, // Recovery data
    /^tahta_pending_answers$/, // Pending answers for recovery
    /^\d+_answers$/, // Test answers with numeric testId
    /^testData_/, // Any test data prefixed keys
    /^qb_/, // Question bank related data
    /^drawing_/, // Drawing history data
    /^whiteboard_/, // Whiteboard state data
    /^canvas_/, // Canvas related data
    /^test_\d+_/, // Test data with numeric ID pattern
    /^answers_\d+/, // Answer data with numeric pattern
    /_questionData$/, // Keys ending with questionData
    /_opticalAnswers$/, // Keys ending with opticalAnswers
    /_eliminatedChoices$/ // Keys ending with eliminatedChoices
  ];
  
  // Scan all localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      // Check against all patterns
      const shouldRemove = patterns.some(pattern => pattern.test(key));
      if (shouldRemove) {
        keysToRemove.push(key);
      }
    }
  }
  
  console.log('📊 Found localStorage keys to clear:', keysToRemove.length);
  keysToRemove.forEach(key => {
    console.log('  🗑️ Removing:', key);
    localStorage.removeItem(key);
  });
  
  console.log('✅ LocalStorage force cleared');
};

/**
 * Cleans up test data for WebView mode
 * Similar to cleanupTestData but with WebView-specific considerations
 */
export const cleanupWebViewTestData = async (testId?: string | null) => {
  console.log('🧹 Starting AGGRESSIVE WebView test data cleanup');
  
  // FIRST: Force clear canvas completely (before other cleanup)
  forceCanvasClear();
  
  // SECOND: Aggressive localStorage cleanup
  forceLocalStorageClear();
  
  // THIRD: Call the main cleanup function
  await cleanupTestData(testId);
  
  // FOURTH: Clear any remaining cached question data from stores
  const whiteboardStore = useWhiteboardStore.getState();
  whiteboardStore.setQuestionDataCache(new Map());
  whiteboardStore.questionPositionMetadata = {};
  
  // FIFTH: Clear any session/memory caches
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
      console.log('✅ Browser caches cleared');
    } catch (error) {
      console.warn('Could not clear browser caches:', error);
    }
  }
  
  console.log('✅ AGGRESSIVE WebView test data cleanup completed');
};